import { clientApi } from 'shared/api/clientApi'
import { Response } from 'shared/models/general'

import type {
  CreateObjectAddressStepResponse,
  CreateObjectBookingStepResponse,
  CreateObjectDescriptionStepResponse,
  CreateObjectDraftRequest,
  CreateObjectDraftResponse,
  CreateObjectDynamicFieldsStepResponse,
  CreateObjectLocationCity,
  CreateObjectLocationRegion,
  CreateObjectPhoto,
  CreateObjectPhotosStepResponse,
  CreateObjectPriceStepResponse,
  CreateObjectRulesStepResponse,
  SaveCreateObjectAddressRequest,
  SaveCreateObjectBookingRequest,
  SaveCreateObjectDescriptionRequest,
  SaveCreateObjectDynamicFieldsRequest,
  SaveCreateObjectPriceRequest,
  SaveCreateObjectRulesRequest,
  UploadCreateObjectPhotosRequest,
  WebviewOwnerRealtyType,
} from './model'

export const KyrgyzstanCreateObjectApi = {
  getRealtyTypes(signal?: AbortSignal) {
    return clientApi.get<Response<WebviewOwnerRealtyType[]>>(
      '/kyrgyzstan/webview-owner/realty-types',
      { signal },
    )
  },

  createDraft(data: CreateObjectDraftRequest) {
    return clientApi.post<Response<CreateObjectDraftResponse>>(
      '/kyrgyzstan/webview-owner/realty-objects/draft',
      data,
    )
  },

  getPhotosStep(draftId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectPhotosStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/photos`,
      { signal },
    )
  },

  completePhotosStep(draftId: string) {
    return clientApi.patch<Response<CreateObjectPhotosStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/photos`,
    )
  },

  uploadPhotos({
    draftId,
    files,
    startSortOrder,
  }: UploadCreateObjectPhotosRequest) {
    const formData = new FormData()

    files.forEach((file, index) => {
      formData.append('file', file)
      formData.append('sort_order', String(startSortOrder + index))
    })

    return clientApi.post<Response<CreateObjectPhoto[]>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/photos`,
      formData,
      {
        adapter: 'fetch',
      },
    )
  },

  deletePhoto(draftId: string, photoId: string) {
    return clientApi.delete<Response<unknown>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/photos/${photoId}`,
    )
  },

  getAddressStep(draftId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectAddressStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/address`,
      { signal },
    )
  },

  saveAddressStep(draftId: string, data: SaveCreateObjectAddressRequest) {
    return clientApi.post<Response<CreateObjectAddressStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/address`,
      data,
    )
  },

  getLocationRegions(signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectLocationRegion[]>>(
      '/kyrgyzstan/webview-owner/locations/regions',
      { signal },
    )
  },

  getLocationCities(regionId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectLocationCity[]>>(
      '/kyrgyzstan/webview-owner/locations/cities',
      {
        params: {
          region_id: regionId,
        },
        signal,
      },
    )
  },

  getDynamicFieldsStep(draftId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectDynamicFieldsStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/dynamic-fields`,
      { signal },
    )
  },

  saveDynamicFieldsStep(
    draftId: string,
    data: SaveCreateObjectDynamicFieldsRequest,
  ) {
    return clientApi.post<Response<CreateObjectDynamicFieldsStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/dynamic-fields`,
      data,
    )
  },

  getDescriptionStep(draftId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectDescriptionStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/description`,
      { signal },
    )
  },

  saveDescriptionStep(
    draftId: string,
    data: SaveCreateObjectDescriptionRequest,
  ) {
    return clientApi.post<Response<CreateObjectDescriptionStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/description`,
      data,
    )
  },

  getPriceStep(draftId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectPriceStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/price`,
      { signal },
    )
  },

  savePriceStep(draftId: string, data: SaveCreateObjectPriceRequest) {
    return clientApi.post<Response<CreateObjectPriceStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/price`,
      data,
    )
  },

  getRulesStep(draftId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectRulesStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/rules`,
      { signal },
    )
  },

  saveRulesStep(draftId: string, data: SaveCreateObjectRulesRequest) {
    return clientApi.post<Response<CreateObjectRulesStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/rules`,
      data,
    )
  },

  getBookingStep(draftId: string, signal?: AbortSignal) {
    return clientApi.get<Response<CreateObjectBookingStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/booking`,
      { signal },
    )
  },

  saveBookingStep(draftId: string, data: SaveCreateObjectBookingRequest) {
    return clientApi.post<Response<CreateObjectBookingStepResponse>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/steps/booking`,
      data,
    )
  },

  submitDraft(draftId: string) {
    return clientApi.post<Response<unknown>>(
      `/kyrgyzstan/webview-owner/realty-objects/draft/${draftId}/submit`,
    )
  },
}
